import React, { useState, useEffect, useMemo } from 'react';
import { vehiculosAPI } from '../../lib/apiRoutes';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const AdminVehiculos = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    familia: '',
    subfamilia: '',
    estado: '',
    soloFavoritos: false
  });

  useEffect(() => {
    loadVehiculos();
  }, []);

  const loadVehiculos = async () => {
    setLoading(true);
    try {
      const response = await vehiculosAPI.list();
      setVehiculos(response.data.vehiculos || []);
    } catch (error) {
      toast.error('Error cargando vehículos');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorito = async (vehiculoId, currentFavorito) => {
    try {
      await vehiculosAPI.toggleFavorito(vehiculoId, !currentFavorito);
      setVehiculos(prev => prev.map(v => 
        v._id === vehiculoId ? { ...v, favorito: !currentFavorito } : v
      ));
      toast.success(currentFavorito ? 'Eliminado de favoritos' : 'Añadido a favoritos');
    } catch (error) {
      toast.error('Error actualizando favorito');
    }
  };

  // Get unique values for filters
  const familias = useMemo(() => {
    const unique = [...new Set(vehiculos.map(v => v.tipo).filter(Boolean))];
    return unique.sort();
  }, [vehiculos]);

  const subfamilias = useMemo(() => {
    const unique = [...new Set(vehiculos.map(v => v.modelo).filter(Boolean))];
    return unique.sort();
  }, [vehiculos]);

  // Filter vehicles
  const filteredVehiculos = useMemo(() => {
    return vehiculos.filter(v => {
      if (filters.soloFavoritos && !v.favorito) return false;
      if (filters.familia && v.tipo !== filters.familia) return false;
      if (filters.subfamilia && v.modelo !== filters.subfamilia) return false;
      if (filters.estado && v.estado !== filters.estado) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        return (
          v.matricula?.toLowerCase().includes(search) ||
          v.marca?.toLowerCase().includes(search) ||
          v.modelo?.toLowerCase().includes(search) ||
          v.tipo?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [vehiculos, filters]);

  // Count stats
  const stats = useMemo(() => ({
    total: vehiculos.length,
    favoritos: vehiculos.filter(v => v.favorito).length,
    operativos: vehiculos.filter(v => v.estado === 'Operativa').length,
    filtrados: filteredVehiculos.length
  }), [vehiculos, filteredVehiculos]);

  return (
    <div data-testid="admin-vehiculos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Vehículos</h1>
          <p className="text-sm text-gray-500 mt-1">
            {stats.total} vehículos · {stats.favoritos} favoritos · {stats.operativos} operativos
          </p>
        </div>
        <Button onClick={loadVehiculos} variant="outline" disabled={loading}>
          {loading ? 'Cargando...' : '🔄 Actualizar'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <span>🔍</span> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <Label>Buscar</Label>
              <Input
                placeholder="Matrícula, marca..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div>
              <Label>Familia (Tipo)</Label>
              <select
                value={filters.familia}
                onChange={(e) => setFilters({ ...filters, familia: e.target.value })}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value="">Todas las familias</option>
                {familias.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Subfamilia (Modelo)</Label>
              <select
                value={filters.subfamilia}
                onChange={(e) => setFilters({ ...filters, subfamilia: e.target.value })}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value="">Todas las subfamilias</option>
                {subfamilias.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Estado</Label>
              <select
                value={filters.estado}
                onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value="">Todos</option>
                <option value="Operativa">Operativa</option>
                <option value="No operativa">No operativa</option>
              </select>
            </div>
            <div className="flex flex-col">
              <Label>Favoritos</Label>
              <Button
                variant={filters.soloFavoritos ? "default" : "outline"}
                onClick={() => setFilters({ ...filters, soloFavoritos: !filters.soloFavoritos })}
                className="mt-1"
              >
                {filters.soloFavoritos ? '⭐ Solo Favoritos' : '☆ Todos'}
              </Button>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-gray-500">
              Mostrando {filteredVehiculos.length} de {vehiculos.length} vehículos
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setFilters({ search: '', familia: '', subfamilia: '', estado: '', soloFavoritos: false })}
            >
              Limpiar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Grid */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
          <p className="text-gray-600">Cargando vehículos...</p>
        </div>
      ) : filteredVehiculos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">🚗</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No hay vehículos que coincidan con los filtros
            </h3>
            <Button variant="outline" onClick={() => setFilters({ search: '', familia: '', subfamilia: '', estado: '', soloFavoritos: false })}>
              Limpiar filtros
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredVehiculos.map((vehiculo) => (
            <Card 
              key={vehiculo._id} 
              className={`hover:shadow-lg transition relative ${
                vehiculo.favorito ? 'ring-2 ring-yellow-400 bg-yellow-50/50' : ''
              }`}
              data-testid={`vehiculo-card-${vehiculo.matricula}`}
            >
              {/* Favorite Button */}
              <button
                onClick={() => toggleFavorito(vehiculo._id, vehiculo.favorito)}
                className="absolute top-3 right-3 text-2xl hover:scale-110 transition z-10"
                title={vehiculo.favorito ? 'Quitar de favoritos' : 'Añadir a favoritos'}
              >
                {vehiculo.favorito ? '⭐' : '☆'}
              </button>
              
              <CardHeader className="pb-2 pr-12">
                <CardTitle className="text-lg font-bold text-primary">
                  {vehiculo.matricula}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Marca:</span>
                    <span className="font-medium">{vehiculo.marca || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Familia:</span>
                    <span className="font-medium text-xs">{vehiculo.tipo || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subfamilia:</span>
                    <span className="font-medium text-xs">{vehiculo.modelo || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Combustible:</span>
                    <span className="font-medium">{vehiculo.combustible || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Centro:</span>
                    <span className="font-medium text-xs">{vehiculo.centro || '-'}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      vehiculo.estado === 'Operativa' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {vehiculo.estado || 'Sin estado'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminVehiculos;
